import React, { useState, useEffect } from 'react';
import { Button, Typography, Stack, Box, MenuItem, Select, FormControl, InputLabel, Grid, TextField, useMediaQuery } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { ToastType, useToast } from '../../../context/toast/ToastContext';
import { useUserContext } from '../../../context/user_context/UserContext';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { Loading } from '../../../components/loading/Loading';
import LoadingData from '../../../components/loading/LoadingData';
import { suggestedDocuments } from './SuggestedDocuments';
import { documentQuestions } from './DocumentQuestions';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, convertInchesToTwip, AlignmentType, HeadingLevel, UnderlineType } from 'docx';
import ConfirmActionModal from '../../../components/modals/ConfirmActionModal';
import { CloudFunctions } from '../../../utils/enums/CloudFunctions';
import { StringKeys } from '../../../resources/strings/StringKeys';

const BiziDocs = () => {
  const { firebaseUser } = useUserContext();
  const { organization } = useOrganizationContext();
  const { showToast } = useToast();
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [customDocumentType, setCustomDocumentType] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState([]);
  const [generatedDocument, setGeneratedDocument] = useState('');
  const [loading, setLoading] = useState(false);
  const [documentGenerated, setDocumentGenerated] = useState(false);
  const { t } = useTranslation();
  const [editorValue, setEditorValue] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false); 
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (selectedDocumentType && documentQuestions[selectedDocumentType]) {
      setAdditionalInfo(Array(documentQuestions[selectedDocumentType].length).fill(''));
    } else if (customDocumentType) {
      setAdditionalInfo(['']);
    }
  }, [selectedDocumentType]);

  const handleDocumentTypeChange = (event) => {
    const value = event.target.value;
    setSelectedDocumentType(value);
    setCustomDocumentType('');
    setAdditionalInfo([]);
  };

  const handleCustomDocumentTypeChange = (event) => {
    setCustomDocumentType(event.target.value);
    setSelectedDocumentType('Other');
    setAdditionalInfo(['']);
  };

  const handleAdditionalInfoChange = (index, value) => {
    const updatedInfo = [...additionalInfo];
    updatedInfo[index] = value;
    setAdditionalInfo(updatedInfo);
  };

  const handleGenerateDocument = async () => {
    setLoading(true);
    setGeneratedDocument('');
    try {
      const functions = getFunctions();
      const generateDocument = httpsCallable(functions, CloudFunctions.GenerateDocument);
      const prompt = t(StringKeys.CREATE_A_DOCUMENT_FOR_ORGANIZATION, {
        document: customDocumentType || t(StringKeys[selectedDocumentType.toUpperCase().replace(/ /g, '_')]),
        orgName: organization.name
      });
      const result = await generateDocument({ prompt, additionalInfo });
      setGeneratedDocument(result.data);
      setEditorValue(result.data);
      showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
      setDocumentGenerated(true);
    } catch (error) {
      console.error("Error generating document:", error);
      showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNewDocument = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmGenerateNewDocument = () => {
    setShowConfirmModal(false);
    setSelectedDocumentType('');
    setCustomDocumentType('');
    setAdditionalInfo([]);
    setGeneratedDocument('');
    setEditorValue('')
    setDocumentGenerated(false);
  };

  const handleEditorChange = (content) => {
    setEditorValue(content); 
  };

  const handleSaveDocumentAsPDF = async () => {
    const editorElement = document.querySelector('.ql-editor');
    if (!editorElement) return;
  
    try {
      const tempStyles = document.createElement('style');
      tempStyles.innerHTML = `
        .ql-editor {
          padding: 20mm !important;
          background: white !important;
          box-shadow: none !important;
        }
        .ql-editor p, .ql-editor h1, .ql-editor h2, .ql-editor h3, .ql-editor ul, .ql-editor ol {
          margin-bottom: 1em !important;
          page-break-inside: avoid !important;
        }
      `;
      document.head.appendChild(tempStyles);
  
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
  
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // 10mm margins
  
      const contentWidth = pageWidth - (2 * margin);
      const contentHeight = pageHeight - (2 * margin);
  
      const canvas = await html2canvas(editorElement, {
        scale: 2, 
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        windowWidth: editorElement.scrollWidth,
        windowHeight: editorElement.scrollHeight,
        onclone: (clonedDoc) => {
          const lists = clonedDoc.querySelectorAll('.ql-editor ul, .ql-editor ol');
          lists.forEach(list => {
            list.style.paddingLeft = '20px';
            list.style.listStylePosition = 'outside';
          });
        }
      });
  
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
  
      const pageRatio = contentHeight / contentWidth;
      const canvasRatio = imgHeight / imgWidth;
      const scaledWidth = contentWidth;
      const scaledHeight = scaledWidth * canvasRatio;
      const totalPages = Math.ceil(scaledHeight / contentHeight);
  
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
  
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
  
        const sourceY = page * (imgHeight / totalPages);
        const sourceHeight = imgHeight / totalPages;
  
        pdf.addImage(
          imgData,
          'JPEG',
          margin,
          margin,
          contentWidth,
          contentHeight,
          undefined,
          'FAST',
          0,
          {
            sourceX: 0,
            sourceY: sourceY,
            sourceWidth: imgWidth,
            sourceHeight: sourceHeight
          }
        );
  
        pdf.setFontSize(10);
        pdf.setTextColor(100);
        pdf.text(
          `Page ${page + 1} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 5,
          { align: 'center' }
        );
      }
  
      pdf.setProperties({
        title: selectedDocumentType || customDocumentType,
        subject: 'Generated by BiziDocs',
        creator: 'BiziDocs',
        author: organization?.name || 'User',
        keywords: 'document, generated',
        creationDate: new Date()
      });
  
      pdf.save(`${selectedDocumentType || customDocumentType}.pdf`);
  
      tempStyles.remove();
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
    }
  };

  const convertQuillDeltaToWord = (editorElement) => {
    if (!editorElement) return null;

    const numbering = {
      config: [
        {
          reference: 'bullet-list',
          levels: [
            {
              level: 0,
              format: 'bullet',
              text: '•',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) }
                }
              }
            }
          ]
        },
        {
          reference: 'numbered-list',
          levels: [
            {
              level: 0,
              format: 'decimal',
              text: '%1.',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) }
                }
              }
            }
          ]
        }
      ]
    };

    const paragraphs = [];
    let currentListType = null;

    Array.from(editorElement.children).forEach((block) => {
      const style = window.getComputedStyle(block);
      
      const processTextRuns = (node) => {
        const runs = [];
        
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.trim()) {
            runs.push(
              new TextRun({
                text: node.textContent,
                bold: style.fontWeight === 'bold' || style.fontWeight >= 600,
                italics: style.fontStyle === 'italic',
                underline: style.textDecoration.includes('underline') ? { type: UnderlineType.SINGLE } : undefined,
              })
            );
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const nodeStyle = window.getComputedStyle(node);
          
          if (node.childNodes.length > 0) {
            node.childNodes.forEach(childNode => {
              const childRuns = processTextRuns(childNode);
              runs.push(...childRuns);
            });
          } else {
            runs.push(
              new TextRun({
                text: node.textContent,
                bold: nodeStyle.fontWeight === 'bold' || nodeStyle.fontWeight >= 600,
                italics: nodeStyle.fontStyle === 'italic',
                underline: nodeStyle.textDecoration.includes('underline') ? { type: UnderlineType.SINGLE } : undefined,
              })
            );
          }
        }
        
        return runs;
      };

      let paragraph;
      
      if (block.tagName === 'UL' || block.tagName === 'OL') {
        currentListType = block.tagName === 'UL' ? 'bullet-list' : 'numbered-list';
        
        Array.from(block.children).forEach((li) => {
          const runs = processTextRuns(li);
          paragraphs.push(
            new Paragraph({
              children: runs,
              numbering: {
                reference: currentListType,
                level: 0,
              },
              spacing: {
                before: 120,
                after: 120,
              },
            })
          );
        });
      } else {
        currentListType = null;
        
        const headingMatch = block.tagName.match(/H(\d)/);
        const heading = headingMatch ? parseInt(headingMatch[1]) : null;
        
        const runs = processTextRuns(block);
        paragraph = new Paragraph({
          children: runs,
          heading: heading ? HeadingLevel[`HEADING_${heading}`] : undefined,
          spacing: {
            before: 120,
            after: 120,
          },
          alignment: style.textAlign === 'center' ? AlignmentType.CENTER :
                    style.textAlign === 'right' ? AlignmentType.RIGHT :
                    AlignmentType.LEFT,
        });
        
        paragraphs.push(paragraph);
      }
    });

    return new Document({
      numbering,
      sections: [{
        properties: {},
        children: paragraphs
      }]
    });
  };

  const handleSaveDocumentAsWord = async () => {
    const editorElement = document.querySelector(".ql-editor");
    
    if (!editorElement) {
      console.error("Editor content not found");
      return;
    }

    try {
      const doc = convertQuillDeltaToWord(editorElement);
      if (!doc) {
        throw new Error("Failed to create Word document");
      }
      
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${selectedDocumentType || customDocumentType}.docx`);
    } catch (error) {
      console.error("Error saving document:", error);
      showToast("Failed to save document. Please try again.", "error");
    }
  };

  return (
    <div className="bizidocs-container">
      {firebaseUser ? (
        <AppSidebar
          content={
            <>
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid item xs={12}>
                  <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    {!isMobile && (
                      <Grid item>
                        <Typography variant="h5">{t(StringKeys.BIZIDOCS)}</Typography>
                        <Typography 
                          variant="subtitle1" 
                          color="text.secondary"
                          sx={{ 
                            mt: 0.5, 
                            mb: 2,
                            maxWidth: '600px',
                            lineHeight: 1.4
                          }}
                        >
                          {t(StringKeys.BIZIDOCS_DESCRIPTION)}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item>
                      <Grid container spacing={2} alignItems="center">
                        {!isMobile && (
                          <>
                            {generatedDocument && (
                              <Grid item>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={handleGenerateNewDocument}
                                >
                                  {t(StringKeys.NEW_DOCUMENT)}
                                </Button>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  {isMobile && (
                    <>
                      <Typography variant="h6">{t(StringKeys.BIZIDOCS)}</Typography>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ 
                          mt: 0.5, 
                          mb: 2,
                          lineHeight: 1.4
                        }}
                      >
                        Generate professional business documents, correspondence, and communications in seconds using AI.
                      </Typography>
                    </>
                  )}
                </Grid>

                {!generatedDocument ? (
                  <Grid item xs={12} sx={{ width: '100%' }}>
                    <Box sx={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
                      {/* Document Type Selection */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>{t(StringKeys.SUGGESTED_DOCUMENTS)}</InputLabel>
                            <Select
                              value={selectedDocumentType}
                              onChange={handleDocumentTypeChange}
                              label={t(StringKeys.SUGGESTED_DOCUMENTS)}
                            >
                              {suggestedDocuments.map((doc, index) => (
                                <MenuItem key={index} value={doc}>
                                  {t(doc.toLowerCase().replace(/ /g, '_'))}
                                </MenuItem>
                              ))}
                              <MenuItem value="Other">{t(StringKeys.OTHER)}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {selectedDocumentType && (
                          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              onClick={handleGenerateDocument}
                              disabled={loading}
                            >
                              {t(StringKeys.GENERATE_DOCUMENT)}
                            </Button>
                          </Grid>
                        )}
                      </Grid>

                      {/* Custom Document Type Input */}
                      {selectedDocumentType === 'Other' && (
                        <Box sx={{ mt: 3, width: '100%' }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            label={t(StringKeys.SPECIFY_DOCUMENT_TYPE)}
                            value={customDocumentType}
                            onChange={handleCustomDocumentTypeChange}
                          />
                        </Box>
                      )}

                      {/* Additional Info Fields */}
                      {(selectedDocumentType || customDocumentType) && (
                        <Box sx={{ mt: 3, width: '100%' }}>
                          {documentQuestions[selectedDocumentType]?.map((question, index) => (
                            <TextField
                              key={index}
                              fullWidth
                              variant="outlined"
                              margin="normal"
                              label={t(question)}
                              value={additionalInfo[index] || ''}
                              onChange={(e) => handleAdditionalInfoChange(index, e.target.value)}
                            />
                          ))}

                          {customDocumentType && (
                            <TextField
                              fullWidth
                              variant="outlined"
                              margin="normal"
                              label={t(StringKeys.PLEASE_PROVIDE_KEY_DETAILS_FOR_YOUR_, {docType: customDocumentType})}
                              value={additionalInfo[0] || ''}
                              onChange={(e) => handleAdditionalInfoChange(0, e.target.value)}
                              multiline
                              rows={4}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Box sx={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
                      <ReactQuill
                        theme="snow"
                        value={editorValue}
                        onChange={handleEditorChange}
                        style={{ minHeight: '200px' }}
                        readOnly={false}
                      />
                      <Box display="flex" justifyContent="flex-end" mt={1}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleSaveDocumentAsWord()}
                        >
                          {t(StringKeys.SAVE_AS_DOC)}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {loading && <LoadingData />}
              </Grid>
              <ConfirmActionModal
                  open={showConfirmModal}
                  onClose={() => setShowConfirmModal(false)}
                  onConfirm={handleConfirmGenerateNewDocument}
                  title={t(StringKeys.START_A_NEW_DOCUMENT)}
                  message={t(StringKeys.ARE_YOU_SURE_YOU_WANT_TO_GENERATE_A_NEW_DOCUMENT_)} />
            </>
          }
        />
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default BiziDocs;
